import { motion } from "framer-motion";
import { ArrowRight, Brain, MessageSquare, Handshake, CheckCircle } from "lucide-react";
import { HeroCTAGroup, ConsultationCTA, PartnershipCTA, CTA } from "./CTA";

/**
 * ConversionFunnel - Progressive disclosure component for user journeys
 *
 * Day 6 Design: CTA Journey Cleanup
 * - Clear conversion funnels
 * - Progressive disclosure
 * - Value-driven next steps
 */

interface FunnelStep {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  cta: React.ReactNode;
  highlight?: boolean;
}

interface ConversionFunnelProps {
  steps: FunnelStep[];
  title?: string;
  className?: string;
}

export function ConversionFunnel({ steps, title, className }: ConversionFunnelProps) {
  return (
    <div className={className}>
      {title && (
        <h3 className="mb-6 text-center font-heading text-xl font-semibold text-foreground">
          {title}
        </h3>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {steps.map((step, index) => (
          <motion.div
            key={step.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className={`rounded-xl border p-6 transition-all hover:shadow-md ${
              step.highlight
                ? "border-accent bg-accent/5 shadow-sm"
                : "border-border bg-card"
            }`}
          >
            <div className="mb-4 flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                step.highlight ? "bg-accent text-accent-foreground" : "bg-secondary text-accent"
              }`}>
                <step.icon className="h-5 w-5" />
              </div>
              {index < steps.length - 1 && (
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <h4 className="mb-2 font-heading text-lg font-semibold text-card-foreground">
              {step.title}
            </h4>
            <p className="mb-4 text-sm text-muted-foreground">
              {step.description}
            </p>
            {step.cta}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// Pre-configured funnels for common user journeys

export function CertificationFunnel() {
  const steps: FunnelStep[] = [
    {
      title: "Discover Requirements",
      description: "Use AI-guided discovery to understand likely certification, compliance, and documentation requirements.",
      icon: Brain,
      cta: <CTA variant="tertiary" to="/tools/compliance-advisor">Start Free Assessment</CTA>,
      highlight: true
    },
    {
      title: "Review Guided Support",
      description: "Need guided execution support? Continue with GetSaarthi.",
      icon: MessageSquare,
      cta: <ConsultationCTA size="sm">Book Consultation</ConsultationCTA>
    },
    {
      title: "Prepare Next Steps",
      description: "Explore certification, compliance, and tender-readiness pathways with guided support through GetSaarthi.",
      icon: CheckCircle,
      cta: <CTA variant="tertiary" to="/services">Explore Services</CTA>
    }
  ];

  return <ConversionFunnel steps={steps} title="Your Certification Journey" />;
}

export function PartnershipFunnel() {
  const steps: FunnelStep[] = [
    {
      title: "Explore Opportunities",
      description: "See how we can collaborate on referrals, delivery, or joint proposals.",
      icon: Handshake,
      cta: <PartnershipCTA size="sm">Submit Enquiry</PartnershipCTA>,
      highlight: true
    },
    {
      title: "Discuss Collaboration",
      description: "Our team reviews your enquiry and reaches out for detailed discussion.",
      icon: MessageSquare,
      cta: <ConsultationCTA size="sm">Contact Directly</ConsultationCTA>
    },
    {
      title: "Build Partnership",
      description: "Review collaboration fit and route suitable opportunities into guided support channels.",
      icon: CheckCircle,
      cta: <CTA variant="tertiary" to="/partnerships">Learn More</CTA>
    }
  ];

  return <ConversionFunnel steps={steps} title="Partnership Development" />;
}

export function ServiceFunnel() {
  const steps: FunnelStep[] = [
    {
      title: "Choose Your Service",
      description: "Browse our comprehensive consulting services and find what you need.",
      icon: CheckCircle,
      cta: <CTA variant="tertiary" to="/services">View All Services</CTA>,
      highlight: true
    },
    {
      title: "Get Personalized Quote",
      description: "Share your requirements so the right support pathway can be reviewed.",
      icon: MessageSquare,
      cta: <ConsultationCTA size="sm">Request Quote</ConsultationCTA>
    },
    {
      title: "Continue With Support",
      description: "Need guided execution support? Continue with GetSaarthi.",
      icon: CheckCircle,
      cta: <CTA variant="tertiary" to="/contact">Get Started</CTA>
    }
  ];

  return <ConversionFunnel steps={steps} title="Service Implementation" />;
}
